import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { useApi, errMsg, fmtDateTime } from "@/lib/api";
import { useMe } from "@/components/portal/MeContext";
import { Input } from "@/components/ui/input";

export const MessageThread = ({ projectId }) => {
  const api = useApi();
  const me = useMe();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const end = useRef(null);

  const load = useCallback(() => api("get", `/projects/${projectId}/messages`).then(setMsgs), [api, projectId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "nearest" });
  }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const m = await api("post", `/projects/${projectId}/messages`, { text });
      setMsgs([...msgs, m]);
      setText("");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="flex h-[440px] flex-col rounded-2xl border border-[#E6E2DA] bg-white" data-testid="message-thread">
      <p className="eyebrow border-b border-[#E6E2DA] px-5 py-4">Messages</p>
      <div className="flex-1 space-y-3 overflow-y-auto p-5">
        {!msgs.length && <p className="text-sm text-[#5C5850]">No messages yet. Say hello!</p>}
        {msgs.map((m) => {
          const mine = m.sender_id === me.user_id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-[#0E1F3F] text-white" : "bg-[#F2EFE9]"}`}>
                <p className={`mb-0.5 text-[10px] font-semibold uppercase tracking-wider ${mine ? "text-white/60" : "text-[#8C877D]"}`}>{m.sender_name} · {m.sender_role === "customer" ? "Customer" : "Firm Foundations"}</p>
                <p className="whitespace-pre-wrap">{m.text}</p>
                <p className={`mt-1 text-[10px] ${mine ? "text-white/50" : "text-[#8C877D]"}`}>{fmtDateTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="flex gap-2 border-t border-[#E6E2DA] p-3">
        <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a message…" data-testid="customer-message-input" />
        <button className="grid w-11 shrink-0 place-items-center rounded-md bg-[#F2B91C] text-[#0E1F3F] hover:bg-[#D9A30F]" data-testid="send-message-btn"><Send size={16} /></button>
      </form>
    </div>
  );
};
